/* Drift Rail — scroll-driven horizontal drift for .rs-drift-rail.
   The track is a NATIVE horizontal scroll container: the user can swipe
   it directly, and page-scroll nudges track.scrollLeft incrementally
   (delta-based). Manual swipes and drift compose — no transform, no
   snap-back, no handoff state; bounds clamp natively. Follows the
   rs-hero-video.liquid triple-guard pattern: module-level idempotency
   guard, per-element guard, and a shopify:section:load re-bind that
   disconnects the prior observer. */
(function () {
  if (window.__rsDriftRailBound) return;
  window.__rsDriftRailBound = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var instances = [];
  /* Every bound track, INCLUDING reduced-motion ones. instances[] holds
     only rails that drift; the resting inset applies to all of them, so
     it needs its own registry or reduced-motion rails would never get
     re-inset on a breakpoint change. */
  var tracks = [];
  var ticking = false;

  /* Frames to keep retrying the inset before accepting what landed.
     ~10 is generous: the tiles carry explicit flex-basis and height, so
     the track reaches full scrollWidth on the first layout regardless
     of image decode. The retry only covers the case where bind() runs
     before that layout has happened at all. */
  var INSET_MAX_FRAMES = 10;

  function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }

  /* ── RESTING INSET ───────────────────────────────────────────────
     The rail rests scrolled slightly in, so the first tile reads as
     already in motion. This was a negative margin-left on the first
     tile until it turned out to be unreachable: content overflowing a
     scroll container's inline-start edge cannot be scrolled back to in
     LTR, so it was a permanent crop rather than an offset.

     THE VALUE LIVES IN CSS, on .rs-drift-rail__track as --dr-inset, and
     is read from the computed style here. Do not hardcode it — the
     breakpoint is expressed once, in the stylesheet. */
  function readInset(track) {
    var v = parseFloat(getComputedStyle(track).getPropertyValue('--dr-inset'));
    return isNaN(v) ? 0 : v;
  }

  /* scrollLeft is silently CLAMPED to (scrollWidth - clientWidth), so an
     assignment made before the track has its full width lands short and
     reports success. Set it, read it back, and retry across frames until
     the value actually sticks. */
  function applyInset(track, inset, onSettled) {
    var frames = 0;

    function attempt() {
      track.scrollLeft = inset;

      if (Math.abs(track.scrollLeft - inset) < 1 || ++frames >= INSET_MAX_FRAMES) {
        /* Record the INTENDED value either way. It is the breakpoint
           comparison key in onResize, not a measurement. */
        track.__rsDriftInset = inset;
        if (onSettled) onSettled();
        return;
      }
      window.requestAnimationFrame(attempt);
    }

    window.requestAnimationFrame(attempt);
  }

  /* Takes an ALREADY-MEASURED rect rather than reading one itself, so a
     frame costs exactly one getBoundingClientRect per instance. */
  function progressNow(rect) {
    var vh = window.innerHeight || document.documentElement.clientHeight;
    return clamp01((vh - rect.top) / (rect.height + vh));
  }

  /* SCROLL GATE. The rail holds still until the section's top edge has
     crossed `gate` of the viewport height (0.75 by default, i.e. the
     top quarter is still empty when motion begins).

     lastProgress is updated UNCONDITIONALLY — outside the gate branch.
     That is the entire point of the gate: pre-gate progress is
     DISCARDED frame by frame rather than banked, so when the gate
     opens the first applied delta is one frame's worth. Move this
     assignment inside the `if` and every pixel of approach travel
     arrives at once as a single lurch. */
  function updateInstance(inst) {
    var rect = inst.section.getBoundingClientRect();
    var p = progressNow(rect);
    var gated = rect.top > window.innerHeight * inst.gate;
    if (!gated && inst.lastProgress !== null) {
      var overflow = inst.track.scrollWidth - inst.track.clientWidth;
      inst.track.scrollLeft += (p - inst.lastProgress) * overflow * inst.travel;
    }
    inst.lastProgress = p;
  }

  /* Module-level scroll: rAF-throttled, drives only intersecting rails. */
  function onScrollTick() {
    ticking = false;
    for (var i = 0; i < instances.length; i++) {
      if (instances[i].active) updateInstance(instances[i]);
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(onScrollTick);
  }

  /* Resize re-anchors (lastProgress = null) so the next frame measures a
     fresh delta instead of jumping by a stale one. */
  function onResize() {
    for (var i = 0; i < instances.length; i++) {
      instances[i].lastProgress = null;
    }

    /* Re-apply the inset ONLY when its computed value actually changed,
       i.e. a breakpoint was crossed. Re-applying on every resize event
       would yank the rail back to the resting offset under a reader who
       is mid-scroll — and on mobile, browser chrome collapsing fires
       resize constantly. */
    for (var j = 0; j < tracks.length; j++) {
      var track = tracks[j];
      var next = readInset(track);
      if (next !== track.__rsDriftInset) applyInset(track, next);
    }
  }

  /* Per-element cleanup — disconnect the section's observer and drop it
     from the registry. Makes re-binding safe (editor reload / re-run). */
  function unbind(section) {
    if (section.__rsDriftIO) {
      section.__rsDriftIO.disconnect();
      section.__rsDriftIO = null;
    }
    for (var i = instances.length - 1; i >= 0; i--) {
      if (instances[i].section === section) instances.splice(i, 1);
    }
    for (var j = tracks.length - 1; j >= 0; j--) {
      if (section.contains(tracks[j])) tracks.splice(j, 1);
    }
    delete section.dataset.rsDriftBound;
  }

  function bind(section) {
    unbind(section); /* per-element guard: clean re-bind */

    var track = section.querySelector('[data-drift-track]');
    if (!track) return; /* empty state — heading only, nothing to move */

    tracks.push(track);

    /* THE INSET IS APPLIED BEFORE THE REDUCED-MOTION RETURN, and that
       ordering is deliberate. A reduced-motion visitor gets no drift,
       but the resting composition is not motion — it is how the section
       is laid out. Returning first would give them a differently
       composed rail, not a calmer one. */
    var inset = readInset(track);

    /* prefers-reduced-motion: the native swipe rail is the base behavior;
       simply don't wire the page-scroll driver. */
    if (reduceMotion.matches) {
      applyInset(track, inset);
      return;
    }

    var inst = {
      section: section,
      track: track,
      travel: parseFloat(section.dataset.driftTravel) || 0.45,
      /* Parsed the same way as travel above. Note the shared caveat:
         `||` treats an explicit 0 as absent and falls back, so a gate
         of exactly 0 is not expressible. 0 would mean "start moving
         the instant the top edge enters the viewport", which is the
         pre-gate behaviour this change exists to remove. */
      gate: parseFloat(section.dataset.driftGate) || 0.75,
      /* Re-anchored below once the inset has settled. */
      lastProgress: progressNow(section.getBoundingClientRect()),
      active: false
    };

    /* Re-anchor AFTER the inset lands. The inset moves scrollLeft
       without moving the page, so a lastProgress captured beforehand is
       still numerically valid — but re-reading it here keeps the two
       writes to scrollLeft strictly ordered, so the first drift delta
       composes with the resting offset instead of racing it. */
    applyInset(track, inset, function () {
      inst.lastProgress = progressNow(section.getBoundingClientRect());
    });

    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        inst.active = entries[i].isIntersecting;
        if (inst.active) updateInstance(inst);
      }
    }, { rootMargin: '100px 0px' });
    io.observe(section);

    section.__rsDriftIO = io;
    section.dataset.rsDriftBound = '1';
    instances.push(inst);
  }

  /* Drop registry entries whose section was removed from the DOM
     (editor tears down + re-injects the section wrapper's contents). */
  function pruneDetached() {
    for (var i = instances.length - 1; i >= 0; i--) {
      if (!document.contains(instances[i].section)) {
        if (instances[i].section.__rsDriftIO) {
          instances[i].section.__rsDriftIO.disconnect();
        }
        instances.splice(i, 1);
      }
    }
    /* Same sweep for the track registry, or a torn-down rail would keep
       being re-inset on every breakpoint change forever. */
    for (var j = tracks.length - 1; j >= 0; j--) {
      if (!document.contains(tracks[j])) tracks.splice(j, 1);
    }
  }

  function bindAll(root) {
    pruneDetached();
    (root || document).querySelectorAll('.rs-drift-rail').forEach(bind);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bindAll(); });
  } else {
    bindAll();
  }

  /* Theme Editor reload: re-bind the reloaded section only. unbind()
     inside bind() resets lastProgress via a fresh instance. */
  document.addEventListener('shopify:section:load', function (e) { bindAll(e.target); });
})();
